import { planPlacementReorder, type PlacementAssignment } from "@afar-research/afar";
import { scopedThreadKey, scopeThreadRef } from "@afar-research/client-runtime/environment";
import type {
  EnvironmentId,
  ScopedThreadRef,
  ThreadId,
  ThreadPlacement,
} from "@afar-research/contracts";
import { useCallback, useRef } from "react";

import { readThreadShell, useServerConfigs } from "../state/entities";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";

/**
 * Which placement writes an AFAR Environment accepts. Capability presence
 * gates the affordance only: ordering itself is computed the same way
 * everywhere, so an Environment that cannot persist an order still renders
 * the same list as one that can.
 */
export interface ThreadPlacementCapabilities {
  readonly pinning: boolean;
  readonly pinReorder: boolean;
  readonly activeReorder: boolean;
}

export function useThreadPlacementCapabilities(): (
  environmentId: EnvironmentId,
) => ThreadPlacementCapabilities {
  const serverConfigs = useServerConfigs();
  return useCallback(
    (environmentId: EnvironmentId) => {
      const capabilities = serverConfigs.get(environmentId)?.environment.capabilities;
      return {
        pinning: capabilities?.threadPinning !== undefined,
        pinReorder: capabilities?.threadPinReorder !== undefined,
        activeReorder: capabilities?.threadActiveReorder !== undefined,
      };
    },
    [serverConfigs],
  );
}

/**
 * Placement commands are revision checked against the thread's own current
 * revision, read at dispatch rather than captured by the caller: a drag can
 * outlive several unrelated updates to the same thread.
 */
export function useThreadPlacement() {
  const pin = useAtomCommand(threadEnvironment.pin, { reportFailure: false });
  const unpin = useAtomCommand(threadEnvironment.unpin, { reportFailure: false });
  const reorderPin = useAtomCommand(threadEnvironment.reorderPin, { reportFailure: false });
  const reorderActive = useAtomCommand(threadEnvironment.reorderActive, { reportFailure: false });
  const capabilitiesOf = useThreadPlacementCapabilities();
  // One placement write per thread at a time. A second drop on the same row
  // would otherwise race the first and could land on a stale revision.
  const inFlight = useRef(new Set<string>());

  const withLease = useCallback(
    async <A>(target: ScopedThreadRef, run: () => Promise<A>): Promise<A | null> => {
      const key = scopedThreadKey(target);
      if (inFlight.current.has(key)) return null;
      inFlight.current.add(key);
      try {
        return await run();
      } finally {
        inFlight.current.delete(key);
      }
    },
    [],
  );

  const pinThread = useCallback(
    (target: ScopedThreadRef, orderKey?: string) =>
      withLease(target, async () => {
        const thread = readThreadShell(target);
        if (!thread || !capabilitiesOf(target.environmentId).pinning) return null;
        return pin({
          environmentId: target.environmentId,
          input: {
            threadId: target.threadId,
            expectedRevision: thread.updatedAt,
            ...(orderKey === undefined ? {} : { orderKey }),
          },
        });
      }),
    [capabilitiesOf, pin, withLease],
  );

  const unpinThread = useCallback(
    (target: ScopedThreadRef) =>
      withLease(target, async () => {
        const thread = readThreadShell(target);
        if (!thread || !capabilitiesOf(target.environmentId).pinning) return null;
        return unpin({
          environmentId: target.environmentId,
          input: { threadId: target.threadId, expectedRevision: thread.updatedAt },
        });
      }),
    [capabilitiesOf, unpin, withLease],
  );

  /**
   * Apply a planned order. Each assignment is one thread's own key on its own
   * Environment, so a failure isolates to that row instead of leaving the
   * block half-written under a single command.
   */
  const applyAssignments = useCallback(
    async (
      assignments: readonly PlacementAssignment[],
      block: "pinned" | "active",
      resolveRef: (key: string) => ScopedThreadRef | null,
    ) => {
      for (const assignment of assignments) {
        const target = resolveRef(assignment.id);
        if (target === null) continue;
        const capabilities = capabilitiesOf(target.environmentId);
        if (block === "pinned" ? !capabilities.pinReorder : !capabilities.activeReorder) continue;
        const thread = readThreadShell(target);
        if (!thread) continue;
        const input = {
          threadId: target.threadId,
          expectedRevision: thread.updatedAt,
          orderKey: assignment.orderKey,
        };
        await (block === "pinned"
          ? reorderPin({ environmentId: target.environmentId, input })
          : reorderActive({ environmentId: target.environmentId, input }));
      }
    },
    [capabilitiesOf, reorderActive, reorderPin],
  );

  return { pinThread, unpinThread, applyAssignments, capabilitiesOf };
}

/** The keys a block's threads currently hold, for the reorder planner. */
export function placementKeysById(
  threads: readonly {
    readonly id: ThreadId;
    readonly environmentId: EnvironmentId;
    readonly placement?: ThreadPlacement | null | undefined;
  }[],
  block: "pinned" | "active",
): ReadonlyMap<string, string | null | undefined> {
  return new Map(
    threads.map((thread) => [
      scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      block === "pinned"
        ? (thread.placement?.pinOrderKey ?? null)
        : (thread.placement?.activeOrderKey ?? null),
    ]),
  );
}

export { planPlacementReorder };
